import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createBranch,
  listBranches,
  updateBranch,
  type BranchRow,
} from "@/shared/api/branches";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { branchColumns } from "./branches-columns";

const branchCreateSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .refine(
      (s) => s === "" || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s),
      "Use lowercase letters, numbers, hyphens",
    ),
});
type BranchCreateInput = z.input<typeof branchCreateSchema>;

/** Owner Settings: add / rename branches — plan/18 + edit in context */
export function BranchesPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const branchesQuery = useQuery({
    queryKey: queryKeys.branches.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listBranches(companySlug!),
  });
  const errorMsg = queryErrorMessage(
    branchesQuery.error,
    branchesQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const createForm = useForm<BranchCreateInput>({
    resolver: zodResolver(branchCreateSchema),
    defaultValues: { name: "", slug: "" },
  });
  const nameWatch = createForm.watch("name");
  const slugWatch = createForm.watch("slug");

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.branches.all(companySlug),
    });
  }

  const create = useMutation({
    mutationFn: async (raw: BranchCreateInput) => {
      const body = branchCreateSchema.parse(raw);
      return createBranch(companySlug!, {
        name: body.name,
        ...(body.slug?.trim() ? { slug: body.slug.trim() } : {}),
      });
    },
    onSuccess: async () => {
      createForm.reset({ name: "", slug: "" });
      setError(null);
      setStatus(null);
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const rename = useMutation({
    mutationFn: async () => {
      if (!companySlug || !editingId) throw new Error("Nothing to save");
      const name = editName.trim();
      if (!name) throw new Error("Name required");
      return updateBranch(companySlug, editingId, { name });
    },
    onSuccess: async (data) => {
      setError(null);
      setStatus(`Renamed to ${data.branch.name}`);
      setEditingId(null);
      setEditName("");
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Rename failed");
    },
  });

  function startEdit(b: BranchRow) {
    setEditingId(b.id);
    setEditName(b.name);
    setError(null);
    setStatus(null);
  }

  const columns = useMemo(
    () =>
      branchColumns({
        savingName: rename.isPending,
        editingId,
        editName,
        setEditName,
        onStartEdit: startEdit,
        onCancelEdit: () => {
          setEditingId(null);
          setEditName("");
        },
        onSaveRename: () => rename.mutate(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [rename.isPending, editingId, editName],
  );

  if (!companySlug) return null;

  const branches = branchesQuery.data?.branches ?? [];
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
    <PageFrame>
      <PageHeader
        title="Branches"
        description="Each branch gets a warehouse. Rename here — slug stays stable for URLs. Add a POS terminal per branch from Terminals."
      />

      {branchesQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading branches…
        </Alert>
      )}
      {!branchesQuery.isPending && branches.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No branches"
            description="Add a branch — each one gets a warehouse."
          />
        </div>
      ) : null}
      {!branchesQuery.isPending && branches.length > 0 ? (
        <DataTable
          listId="branches"
          companySlug={companySlug}
          columns={columns}
          data={branches}
          getRowId={(r) => r.id}
          manualSorting={false}
        />
      ) : null}

      <form
        className="mt-8 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={createForm.handleSubmit((values) => create.mutate(values))}
      >
        <h2 className="m-0 text-sm font-semibold">Add branch</h2>
        <div>
          <Label htmlFor="branch-name">Name</Label>
          <Input
            id="branch-name"
            className="mt-1"
            placeholder="e.g. North Plaza"
            {...createForm.register("name", {
              onChange: (e) => {
                const next = e.target.value as string;
                const currentSlug = slugWatch ?? "";
                if (!currentSlug || currentSlug === slugify(nameWatch ?? "")) {
                  createForm.setValue("slug", slugify(next), {
                    shouldValidate: false,
                  });
                }
              },
            })}
          />
          {createForm.formState.errors.name && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">
              {createForm.formState.errors.name.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="branch-slug">Slug</Label>
          <Input
            id="branch-slug"
            className="mt-1 font-mono"
            placeholder="north-plaza"
            {...createForm.register("slug", {
              setValueAs: (v: string) => v.toLowerCase(),
            })}
          />
          {createForm.formState.errors.slug && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">
              {createForm.formState.errors.slug.message}
            </p>
          )}
        </div>
        <Button
          type="submit"
          pending={create.isPending}
          pendingLabel="Adding…"
        >
          Add
        </Button>
      </form>

      {status && (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      )}
      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <p className="mt-8 flex flex-wrap gap-3 text-sm">
        <Link
          to={`/c/${companySlug}/settings/branches/access`}
          className="no-underline"
        >
          Branch access
        </Link>
        <Link to={`/c/${companySlug}/pos`} className="no-underline">
          POS terminals
        </Link>
        <Link to={`/c/${companySlug}`} className="no-underline">
          ← Company
        </Link>
      </p>
    </PageFrame>
  );
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
